"use client"

import { memo, useMemo, useState, useRef, useEffect, type KeyboardEvent } from "react"
import { Box, Text, Group } from "@forestsync/ui"
import { useTranslations } from "next-intl"
import type { OrderBookLevel } from "@/lib/queries"

type UserOrderInfo = {
  id: bigint
  side: number        // 1=buy, 2=sell
  orderType: number   // 1=LIMIT, 2=MARKET, 3=STOP
  price: number | null
  volume: number
  filledVolume: number
  status: number      // 1=OPEN, 2=PARTIAL, 3=FILLED
}

type UnifiedLevel = {
  price: number
  bidVol: number
  askVol: number
  userBuyOrders: UserOrderInfo[]
  userSellOrders: UserOrderInfo[]
}

export interface OrderBookProps {
  bids: OrderBookLevel[]
  asks: OrderBookLevel[]
  lastPrice?: number
  referencePrice?: number
  precision?: number
  userOrders?: UserOrderInfo[]
  onQuickOrder?: (side: "buy" | "sell", price: number, volume: number) => void
  onCancelOrder?: (id: bigint) => void
  isPending?: boolean
}

const ROW_HEIGHT = 18
const GRID_COLS = "40px 52px 66px 52px 40px 1fr"

function generateLadder(center: number, pct: number, prec: number): number[] {
  const result: number[] = []
  let p = parseFloat((Math.ceil(center * (1 - pct) / prec) * prec).toFixed(8))
  const hi = center * (1 + pct)
  while (p <= hi + prec * 0.001) {
    result.push(p)
    p = parseFloat((p + prec).toFixed(8))
  }
  return result.reverse()
}

function fmtVol(n: number) {
  return n > 0 ? Math.round(n).toLocaleString() : ""
}

function isBoldPrice(price: number) {
  return Math.abs(price * 2 - Math.round(price * 2)) < 0.01
}

function orderTypeLabel(orderType: number): string {
  if (orderType === 3) return "STP"
  if (orderType === 2) return "MKT"
  return "LIM"
}

function chipVolume(o: UserOrderInfo): string {
  if (o.status === 3) return String(Math.round(o.filledVolume))
  if (o.status === 2) return `${Math.round(o.filledVolume)}/${Math.round(o.volume)}`
  return String(Math.round(o.volume - o.filledVolume))
}

function chipColors(status: number): { bg: string; border: string; text: string } {
  if (status === 3) return {
    bg: "rgba(234,179,8,0.15)",
    border: "var(--mantine-color-yellow-4)",
    text: "var(--mantine-color-yellow-4)",
  }
  if (status === 2) return {
    bg: "rgba(251,146,60,0.15)",
    border: "var(--mantine-color-orange-4)",
    text: "var(--mantine-color-orange-4)",
  }
  return {
    bg: "rgba(59,130,246,0.15)",
    border: "var(--mantine-color-blue-4)",
    text: "var(--mantine-color-blue-3)",
  }
}

/* ── Inline click-to-edit input cell ───────────────────────────────────────── */

const TradeCell = memo(function TradeCell({
  price,
  side,
  marketVol,
  onSubmit,
  disabled,
}: {
  price: number
  side: "buy" | "sell"
  marketVol: number
  onSubmit?: (side: "buy" | "sell", price: number, volume: number) => void
  disabled?: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState("")
  const isBuy = side === "buy"
  const color = isBuy ? "var(--mantine-color-green-6)" : "var(--mantine-color-red-6)"
  const borderColor = isBuy ? "var(--mantine-color-green-8)" : "var(--mantine-color-red-8)"

  function submit() {
    const vol = Math.floor(Number(value))
    if (vol > 0 && onSubmit) onSubmit(side, price, vol)
    setValue("")
    setEditing(false)
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") submit()
    if (e.key === "Escape") { setValue(""); setEditing(false) }
  }

  if (editing) {
    return (
      <input
        autoFocus
        type="number"
        min={0}
        step={1}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { setValue(""); setEditing(false) }}
        disabled={disabled}
        aria-label={`${side} qty at €${price}`}
        style={{
          width: "100%",
          height: ROW_HEIGHT,
          border: `1px solid ${borderColor}`,
          background: "var(--mantine-color-dark-7)",
          textAlign: "center",
          fontSize: 11,
          fontFamily: "var(--mantine-font-family-monospace)",
          color,
          outline: "none",
          padding: "0 2px",
          boxSizing: "border-box",
        }}
      />
    )
  }

  const hasVol = marketVol > 0
  return (
    <div
      onClick={() => onSubmit && setEditing(true)}
      style={{
        height: ROW_HEIGHT,
        lineHeight: `${ROW_HEIGHT}px`,
        textAlign: isBuy ? "right" : "left",
        padding: isBuy ? "0 4px 0 0" : "0 0 0 4px",
        fontSize: 11,
        fontFamily: "var(--mantine-font-family-monospace)",
        color: hasVol ? color : "transparent",
        cursor: onSubmit ? "text" : "default",
        userSelect: "none",
      }}
    >
      {hasVol ? fmtVol(marketVol) : onSubmit ? "·" : ""}
    </div>
  )
})

/* ── User order chip (B.ORDER / S.ORDER) ───────────────────────────────────── */

const UserOrderChip = memo(function UserOrderChip({
  orders,
  side,
  onCancel,
}: {
  orders: UserOrderInfo[]
  side: "buy" | "sell"
  onCancel?: (id: bigint) => void
}) {
  if (orders.length === 0) return null

  const o = orders[0]
  const { bg, border, text } = chipColors(o.status)
  const isBuy = side === "buy"
  const canCancel = onCancel && o.status !== 3

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: isBuy ? "flex-end" : "flex-start",
        height: "100%",
        overflow: "hidden",
        padding: isBuy ? "0 2px 0 0" : "0 0 0 2px",
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 2,
          background: bg,
          border: `1px solid ${border}`,
          borderRadius: 3,
          padding: "0 3px",
          height: 13,
          overflow: "hidden",
          maxWidth: "100%",
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 9, fontWeight: 700, color: text, lineHeight: 1 }}>
          {orderTypeLabel(o.orderType)}
        </span>
        <span style={{ fontSize: 10, fontFamily: "var(--mantine-font-family-monospace)", color: text, lineHeight: 1 }}>
          {chipVolume(o)}
        </span>
        {canCancel && (
          <button
            onClick={(e) => { e.stopPropagation(); onCancel(o.id) }}
            title="Cancel order"
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 0,
              color: text,
              fontSize: 10,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
              opacity: 0.8,
            }}
          >
            ×
          </button>
        )}
      </div>
    </div>
  )
})

/* ── Single unified row ─────────────────────────────────────────────────────── */

const OrderBookRow = memo(function OrderBookRow({
  level,
  maxVolume,
  bestBid,
  bestAsk,
  onQuickOrder,
  onCancelOrder,
  isPending,
}: {
  level: UnifiedLevel
  maxVolume: number
  bestBid: number
  bestAsk: number
  onQuickOrder?: (side: "buy" | "sell", price: number, volume: number) => void
  onCancelOrder?: (id: bigint) => void
  isPending?: boolean
}) {
  const isBidSide = bestBid > 0 && level.price <= bestBid
  const isAskSide = bestAsk > 0 && level.price >= bestAsk
  const bold = isBoldPrice(level.price)

  const domVol = isBidSide ? level.bidVol : isAskSide ? level.askVol : 0
  const histPct = maxVolume > 0 ? (domVol / maxVolume) * 100 : 0
  const barColor = isBidSide
    ? "var(--mantine-color-green-9)"
    : isAskSide
    ? "var(--mantine-color-red-9)"
    : "transparent"

  const priceColor = isBidSide
    ? "var(--mantine-color-green-5)"
    : isAskSide
    ? "var(--mantine-color-red-5)"
    : "var(--mantine-color-dimmed)"

  const hasActivity =
    level.bidVol > 0 ||
    level.askVol > 0 ||
    level.userBuyOrders.length > 0 ||
    level.userSellOrders.length > 0

  const allUserOrders = [...level.userBuyOrders, ...level.userSellOrders]
  const hasFilled = allUserOrders.some(o => o.status === 3)
  const hasPartial = !hasFilled && allUserOrders.some(o => o.status === 2)
  const rowBg = hasFilled
    ? "color-mix(in srgb, var(--mantine-color-yellow-4) 10%, transparent)"
    : hasPartial
    ? "color-mix(in srgb, var(--mantine-color-orange-4) 8%, transparent)"
    : undefined

  return (
    <div
      role="row"
      style={{
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        height: ROW_HEIGHT,
        lineHeight: `${ROW_HEIGHT}px`,
        alignItems: "center",
        borderBottom: "1px solid var(--mantine-color-default-border)",
        opacity: hasActivity ? 1 : 0.35,
        background: rowBg,
      }}
    >
      {/* Col 1: B.ORDER — user's resting buy orders */}
      <div role="gridcell" style={{ height: "100%", overflow: "hidden" }}>
        <UserOrderChip orders={level.userBuyOrders} side="buy" onCancel={onCancelOrder} />
      </div>

      {/* Col 2: BUY — market bid depth + click-to-buy */}
      <div role="gridcell" style={{ height: "100%", overflow: "hidden" }}>
        <TradeCell
          price={level.price}
          side="buy"
          marketVol={level.bidVol}
          onSubmit={onQuickOrder}
          disabled={isPending}
        />
      </div>

      {/* Col 3: PRICE */}
      <div
        role="gridcell"
        style={{
          textAlign: "center",
          padding: "0 2px",
          color: priceColor,
          fontWeight: bold ? 700 : 400,
          fontSize: 11,
          fontFamily: "var(--mantine-font-family-monospace)",
        }}
      >
        {level.price.toFixed(2)}
      </div>

      {/* Col 4: SELL — market ask depth + click-to-sell */}
      <div role="gridcell" style={{ height: "100%", overflow: "hidden" }}>
        <TradeCell
          price={level.price}
          side="sell"
          marketVol={level.askVol}
          onSubmit={onQuickOrder}
          disabled={isPending}
        />
      </div>

      {/* Col 5: S.ORDER — user's resting sell orders */}
      <div role="gridcell" style={{ height: "100%", overflow: "hidden" }}>
        <UserOrderChip orders={level.userSellOrders} side="sell" onCancel={onCancelOrder} />
      </div>

      {/* Col 6: EXEC DAY — depth histogram */}
      <div role="gridcell" style={{ height: "100%", display: "flex", alignItems: "center", padding: "0 4px" }}>
        <div style={{ flex: 1, height: 8 }}>
          <div
            style={{
              width: `${histPct}%`,
              height: "100%",
              backgroundColor: barColor,
              borderRadius: 1,
              transition: "width 120ms ease",
            }}
          />
        </div>
      </div>
    </div>
  )
})

/* ── Main component ─────────────────────────────────────────────────────────── */

export function OrderBook({
  bids,
  asks,
  lastPrice,
  referencePrice,
  precision,
  userOrders,
  onQuickOrder,
  onCancelOrder,
  isPending,
}: OrderBookProps) {
  const t = useTranslations("trading")
  const containerRef = useRef<HTMLDivElement>(null)
  const spreadRef = useRef<HTMLDivElement>(null)
  const scrolledRef = useRef(false)

  const bestBid = bids.length > 0 ? Math.max(...bids.map((b) => b.price)) : 0
  const bestAsk = asks.length > 0 ? Math.min(...asks.map((a) => a.price)) : 0
  const spread = bestAsk > 0 && bestBid > 0 ? bestAsk - bestBid : 0
  const spreadPct = bestAsk > 0 ? (spread / bestAsk) * 100 : 0
  const totalBidVol = bids.reduce((s, b) => s + b.volume, 0)
  const totalAskVol = asks.reduce((s, a) => s + a.volume, 0)

  const { levels, globalMaxVolume, spreadIdx, snappedBestAsk, snappedBestBid } = useMemo(() => {
    const prec = precision ?? 1
    const center = lastPrice ?? (bestBid > 0 && bestAsk > 0 ? (bestBid + bestAsk) / 2 : (bestAsk || bestBid || referencePrice || null))

    // Snap a raw price to the nearest precision bucket (floor)
    const snap = (p: number) => parseFloat((Math.floor(p / prec) * prec).toFixed(8))

    // Aggregate bids/asks into precision buckets
    const bidMap = new Map<number, number>()
    for (const b of bids) {
      const bucket = snap(b.price)
      bidMap.set(bucket, (bidMap.get(bucket) ?? 0) + b.volume)
    }
    const askMap = new Map<number, number>()
    for (const a of asks) {
      const bucket = snap(a.price)
      askMap.set(bucket, (askMap.get(bucket) ?? 0) + a.volume)
    }

    // Group user orders by snapped price+side
    const userBuyMap = new Map<number, UserOrderInfo[]>()
    const userSellMap = new Map<number, UserOrderInfo[]>()
    userOrders?.forEach((o) => {
      if (o.price === null) return
      const bucket = snap(o.price)
      if (o.side === 1) {
        if (!userBuyMap.has(bucket)) userBuyMap.set(bucket, [])
        userBuyMap.get(bucket)!.push(o)
      } else {
        if (!userSellMap.has(bucket)) userSellMap.set(bucket, [])
        userSellMap.get(bucket)!.push(o)
      }
    })

    // Build price ladder from snapped/bucketed prices only — no raw off-grid prices
    const extraBuckets = [
      ...bidMap.keys(), ...askMap.keys(),
      ...userBuyMap.keys(), ...userSellMap.keys(),
    ]
    let allPrices: number[]
    if (center !== null) {
      const ladder = generateLadder(center, 0.30, prec)
      allPrices = [...new Set([...ladder, ...extraBuckets])].sort((a, b) => b - a)
    } else {
      allPrices = [...new Set(extraBuckets)].sort((a, b) => b - a)
    }

    const unified: UnifiedLevel[] = allPrices.map((price) => ({
      price,
      bidVol: bidMap.get(price) ?? 0,
      askVol: askMap.get(price) ?? 0,
      userBuyOrders: userBuyMap.get(price) ?? [],
      userSellOrders: userSellMap.get(price) ?? [],
    }))

    const maxVol = Math.max(...unified.map((l) => Math.max(l.bidVol, l.askVol)), 1)

    // Snap best prices to the precision grid so spread index and side coloring
    // align with the bucketed ladder (raw price e.g. 52.84 snaps to bucket 52.80)
    const snappedBestAsk = bestAsk > 0 ? snap(bestAsk) : 0
    const snappedBestBid = bestBid > 0 ? snap(bestBid) : 0

    // Spread row index: first price at/below the best bid (or best ask if one-sided)
    let sIdx = -1
    if (bestBid > 0 && bestAsk > 0) {
      sIdx = unified.findIndex((l) => l.price < snappedBestAsk && l.price > snappedBestBid)
      if (sIdx === -1) sIdx = unified.findIndex((l) => l.price <= snappedBestBid)
    } else if (bestBid > 0) {
      sIdx = unified.findIndex((l) => l.price <= snappedBestBid)
    } else if (bestAsk > 0) {
      sIdx = unified.findIndex((l) => l.price <= snappedBestAsk)
    }

    return { levels: unified, globalMaxVolume: maxVol, spreadIdx: sIdx, snappedBestAsk, snappedBestBid }
  }, [bids, asks, lastPrice, precision, userOrders, bestBid, bestAsk])

  // Scroll to show spread (or best price if one-sided) on first load
  useEffect(() => {
    if (scrolledRef.current || levels.length === 0 || spreadIdx < 0) return
    const timer = setTimeout(() => {
      const container = containerRef.current
      if (!container || container.clientHeight < 100) return
      const spreadTop = (spreadIdx) * ROW_HEIGHT + 28 // 28px = header
      container.scrollTop = Math.max(0, spreadTop - container.clientHeight / 2)
      scrolledRef.current = true
    }, 150)
    return () => clearTimeout(timer)
  }, [levels.length, spreadIdx])

  return (
    <Box
      ref={containerRef}
      role="grid"
      aria-label={t("orderBook")}
      style={{ overflow: "auto", height: "100%" }}
    >
      {/* Sticky header */}
      <div
        role="row"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          display: "grid",
          gridTemplateColumns: GRID_COLS,
          padding: "3px 0",
          borderBottom: "2px solid var(--mantine-color-default-border)",
          background: "var(--mantine-color-body)",
          fontSize: 10,
          fontFamily: "var(--mantine-font-family-monospace)",
        }}
      >
        <Text size="xs" c="green" ta="right" role="columnheader" pr={2} fw={600} ff="monospace">
          {fmtVol(totalBidVol) || "0"}
        </Text>
        <Text size="xs" c="dimmed" ta="center" role="columnheader" ff="monospace">
          {t("buy")}
        </Text>
        <Text size="xs" c="dimmed" ta="center" role="columnheader">
          {spread > 0 ? `${spread.toFixed(2)} (${spreadPct.toFixed(1)}%)` : t("price")}
        </Text>
        <Text size="xs" c="dimmed" ta="center" role="columnheader" ff="monospace">
          {t("sell")}
        </Text>
        <Text size="xs" c="red" ta="left" role="columnheader" pl={2} fw={600} ff="monospace">
          {fmtVol(totalAskVol) || "0"}
        </Text>
        <Text size="xs" c="dimmed" role="columnheader" px={4}>
          {t("depth")}
        </Text>
      </div>

      {/* Unified price ladder */}
      {levels.map((level, idx) => {
        // Collapse empty rows in the spread gap — the spread divider already represents this gap
        const inSpreadGap = bestBid > 0 && bestAsk > 0
          && level.price > bestBid && level.price < bestAsk
          && level.bidVol === 0 && level.askVol === 0
          && level.userBuyOrders.length === 0 && level.userSellOrders.length === 0

        // Skip gap rows that aren't the first (where the spread divider anchors)
        if (inSpreadGap && idx !== spreadIdx) return null

        return (
          <div key={level.price}>
            {/* Spread separator — show once at the top of the gap */}
            {idx === spreadIdx && spread > 0 && (
              <div ref={spreadRef}>
                <Group
                  justify="space-between"
                  py={2}
                  px={6}
                  style={{
                    borderBottom: "1px solid var(--mantine-color-default-border)",
                    background: "var(--mantine-color-default-hover)",
                    fontFamily: "var(--mantine-font-family-monospace)",
                  }}
                >
                  <Text size="xs" c="red.5" fw={500}>Ask €{bestAsk.toFixed(2)}</Text>
                  <Text size="xs" c="dimmed">
                    {spread.toFixed(2)} ({spreadPct.toFixed(2)}%)
                  </Text>
                  <Text size="xs" c="green.5" fw={500}>Bid €{bestBid.toFixed(2)}</Text>
                </Group>
              </div>
            )}
            {/* Don't render the empty gap row itself — only the divider above */}
            {!inSpreadGap && (
              <OrderBookRow
                level={level}
                maxVolume={globalMaxVolume}
                bestBid={snappedBestBid}
                bestAsk={snappedBestAsk}
                onQuickOrder={onQuickOrder}
                onCancelOrder={onCancelOrder}
                isPending={isPending}
              />
            )}
          </div>
        )
      })}
    </Box>
  )
}
